import React, { useState, memo } from 'react';
import { View, Text, StyleSheet, Pressable, Image } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { CommunityResource, ICommunityResourceFile } from '@/types/residents/communityResource';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { ResourceIcon } from '@/components/residents/resources/ResourceIcon';
import { ImageViewerModal } from '@/components/common/ImageViewerModal';
import { GlassCard } from '../GlassCard';
import { openExternalUrl } from '@/utils/common/linking';
import { getFileKind } from '@/utils/common/file';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import type { ThemeContextValue } from '@/hooks/residents/useThemeColors';

interface ResourceGlassPanelProps {
  resource: CommunityResource | null;
  open: boolean;
  onClose: () => void;
}

const ResourceGlassPanelBase: React.FC<ResourceGlassPanelProps> = ({ resource, open, onClose }) => {
  const { colors } = useThemeColors();
  const styles = useThemedStyles(makeStyles);
  const [linkHovered, setLinkHovered] = useState(false);
  const [hoveredFileId, setHoveredFileId] = useState<number | null>(null);
  const [imageViewerUri, setImageViewerUri] = useState<string | null>(null);
  const [thumbFailed, setThumbFailed] = useState<Set<number>>(new Set());

  return (
    <>
    <DesktopSidePanel visible={open} onClose={onClose} width={520}>
      {resource && (
        <View style={[styles.panelBody, { backgroundColor: colors.bg.gradientStart }]}>
          <Pressable onPress={onClose} accessibilityRole="button" style={styles.closeRow}>
            <Icon name="close-outline" size={20} color={colors.text.muted} />
          </Pressable>

          <View style={[styles.iconCircle, { backgroundColor: colors.accent.blueBgLight }]}>
            <ResourceIcon
              iconKey={resource.icon || resource.iconName}
              size={22}
              color={colors.text.primary}
            />
          </View>

          <Text style={[styles.title, { color: colors.text.primary }]}>{resource.name || resource.title}</Text>
          {!!resource.description && (
            <Text style={[styles.subtitle, { color: colors.text.dim }]}>{resource.description}</Text>
          )}

          <View style={styles.sections}>
            {!!resource.content && (
              <GlassCard borderRadius={12} padding={16}>
                <Text style={[styles.infoBannerText, { color: colors.text.secondary }]}>{resource.content}</Text>
              </GlassCard>
            )}

            {!!resource.url && (
              <Pressable
                style={[
                  styles.linkRow,
                  { backgroundColor: colors.card.bg, borderColor: colors.card.border },
                  linkHovered && { borderColor: PRIMITIVE_COLORS.resioBlue },
                ]}
                onHoverIn={() => setLinkHovered(true)}
                onHoverOut={() => setLinkHovered(false)}
                onPress={async () => {
                  await openExternalUrl(resource.url!);
                }}
              >
                <Icon name="link-outline" size={20} color={colors.text.primary} style={styles.linkIcon} />
                <Text style={[styles.linkText, { color: colors.text.primary }]} numberOfLines={1}>{resource.url}</Text>
              </Pressable>
            )}

            {(resource.files || []).map((file: ICommunityResourceFile) => {
              const kind = getFileKind(file);
              const showThumb = kind === 'image' && !thumbFailed.has(file.id);
              return (
                <Pressable
                  key={file.id}
                  style={[
                    styles.linkRow,
                    { backgroundColor: colors.card.bg, borderColor: colors.card.border },
                    hoveredFileId === file.id && { borderColor: PRIMITIVE_COLORS.resioBlue },
                  ]}
                  onHoverIn={() => setHoveredFileId(file.id)}
                  onHoverOut={() => setHoveredFileId((prev) => (prev === file.id ? null : prev))}
                  onPress={async () => {
                    if (kind === 'image') {
                      setImageViewerUri(file.fileUrl);
                      return;
                    }
                    await openExternalUrl(file.fileUrl);
                  }}
                >
                  {showThumb ? (
                    <Image
                      source={{ uri: file.fileUrl }}
                      style={[styles.thumbnail, styles.linkIcon]}
                      onError={() =>
                        setThumbFailed((prev) => {
                          const next = new Set(prev);
                          next.add(file.id);
                          return next;
                        })
                      }
                    />
                  ) : (
                    <RESIDENTS_SVG_COMPONENTS.DOCUMENT
                      width={20}
                      height={20}
                      color={colors.text.primary}
                      style={styles.linkIcon}
                    />
                  )}
                  <Text style={[styles.linkText, { color: colors.text.primary }]} numberOfLines={1}>
                    {file.label || `File ${file.id}`}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      )}
    </DesktopSidePanel>
    <ImageViewerModal
      visible={imageViewerUri !== null}
      images={imageViewerUri ? [imageViewerUri] : []}
      onClose={() => setImageViewerUri(null)}
    />
    </>
  );
};

ResourceGlassPanelBase.displayName = 'ResourceGlassPanel';

export const ResourceGlassPanel = memo(ResourceGlassPanelBase);

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  panelBody: {
    flex: 1,
    padding: REDESIGN_SPACING.cardPadding,
  },
  closeRow: {
    alignSelf: 'flex-end',
    marginBottom: 16,
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'flex-start',
    marginBottom: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    marginTop: 2,
  },
  subtitle: {
    ...REDESIGN_TYPOGRAPHY.body,
    marginTop: 6,
  },
  sections: {
    marginTop: 20,
    gap: 12,
  },
  infoBannerText: {
    ...REDESIGN_TYPOGRAPHY.body,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
  },
  linkIcon: {
    marginRight: 12,
  },
  thumbnail: {
    width: 40,
    height: 40,
    borderRadius: 8,
    backgroundColor: semantic.background.tertiary,
  },
  linkText: {
    ...REDESIGN_TYPOGRAPHY.body,
    flexShrink: 1,
  },
});
